import get from "lodash/get";
import React, { FC, useEffect } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Flex } from "antd";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGetLocationData } from "~/hooks/locationData";
import { onScrollBottomSubdivision } from "~/hooks/selectSubdivisionValue";
import {
  SubdivisionStatus,
  useGetSubdivisionQuery,
  useSubdivisionCreateMutation,
  useSubdivisionEditMutation,
} from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";
import { Field } from "~/shared/ui/Field";

interface SubdivisionFormProps {
  setCloseModal: () => void;
  refetch: () => void;
  subdivision?;
  formId: string;
}

const SubdivisionForm: FC<SubdivisionFormProps> = ({
  setCloseModal,
  refetch,
  subdivision,
  formId,
}) => {
  const { t } = useTranslation();
  const { openModal } = useGetLocationData();
  const isEdit = openModal === "Edit";

  const {
    divisionsForSelect,
    onScrollBottomDivisions,
    searchDivision,
    GetSubdivisionLoading,
  } = onScrollBottomSubdivision();

  const schema = yup.object().shape({
    ...(!isEdit && {
      code: yup
        .string()
        .test(
          "code",
          t("validation.required"),
          () => !watch("code") || !!watch("code").trim(),
        )
        .max(250, t("validation.more250"))
        .required(t("validation.required")),
    }),
    name: yup
      .string()
      .test(
        "name",
        t("validation.required"),
        () => !watch("name") || !!watch("name").trim(),
      )
      .max(250, t("validation.more250"))
      .required(t("validation.required")),
  });

  const { handleSubmit, control, watch, errors, setError, clearErrors } =
    useForm({
      defaultValues: {
        ...subdivision,
        isWorked: subdivision
          ? subdivision.status === SubdivisionStatus.Working
          : false,
        IncludedInDivision: subdivision?.parentId
          ? {
              value: subdivision.parentId,
              label: subdivision.Parent?.name,
            }
          : undefined,
      },
      resolver: yupResolver(schema),
    });

  const codeValue = watch("code");

  const subdivisionCheck = useGetSubdivisionQuery({
    variables: {
      code: codeValue,
    },
    fetchPolicy: "no-cache",
    skip: isEdit || !codeValue,
  });

  const subdivisionCodeIsExist =
    !!codeValue &&
    get(subdivisionCheck.data, "SubdivisionGetter.get.items", []).some(
      (el) => el.code && el.code === codeValue,
    );

  useEffect(() => {
    if (isEdit) {
      return;
    }
    if (subdivisionCodeIsExist) {
      setError("code", {
        type: "uniqueness",
        message: t("validation.unique"),
      });
    } else if (errors["code"]?.type === "uniqueness") {
      clearErrors("code");
    }
  }, [subdivisionCodeIsExist]);

  const { notifySuccess, notifyError } = useNotify();

  const [setSubdivisionCreateMutation] = useSubdivisionCreateMutation({
    onCompleted() {
      refetch();
      setCloseModal();
      notifySuccess(t("common.adding_was_successful"));
    },
    onError(vals) {
      notifyError(vals["message"]);
    },
  });

  const [setSubdivisionEditMutation] = useSubdivisionEditMutation({
    onCompleted() {
      refetch();
      setCloseModal();
      notifySuccess(t("common.сhange_was_successful"));
    },
    onError(vals) {
      notifyError(vals["message"]);
    },
  });

  const onSubmit = (data) => {
    const { code, name, isWorked, IncludedInDivision } = data;

    const status = isWorked
      ? SubdivisionStatus.Working
      : SubdivisionStatus.Archive;
    const parentId = IncludedInDivision?.value;

    isEdit
      ? setSubdivisionEditMutation({
          variables: {
            id: String(subdivision.id),
            name: String(name),
            status,
            parentId,
          },
        })
      : setSubdivisionCreateMutation({
          variables: {
            code: String(code),
            name: String(name),
            status,
            parentId,
          },
        });
  };

  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 16) {
      onScrollBottomDivisions();
    }
  };

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Flex vertical gap={10}>
        {!isEdit && (
          <Field
            type="input"
            name="code"
            control={control}
            label={t("editorSubdivision.code")}
            error={errors["code"]?.message}
          />
        )}
        <Field
          type="input"
          name="name"
          control={control}
          label={t("editorSubdivision.name")}
          error={errors["name"]?.message}
        />
        <Field
          type="checkbox"
          name="isWorked"
          control={control}
          label={t("editorSubdivision.isWorked")}
        />
        <Field
          type="select"
          name="IncludedInDivision"
          control={control}
          label={t("editorSubdivision.IncludedInDivision")}
          placeholder={t("editorSubdivision.status_not_select")}
          options={divisionsForSelect}
          labelInValue
          showSearch
          filterOption={false}
          onSearch={searchDivision}
          onPopupScroll={handlePopupScroll}
          loading={GetSubdivisionLoading}
          error={errors["IncludedInDivision"]?.message}
        />
      </Flex>
    </form>
  );
};

export default SubdivisionForm;
